import Editor, { useMonaco, loader } from '@monaco-editor/react';
import React, { useState } from 'react';
import styles from './MonacoExpressionBuilder.module.scss';
import { UniStorageFactory } from '../../helpers/uni-storage';
import { useTabFocusEffect } from '../../components/keep-alive/useTabs';
import { useTabId } from '../../components/keep-alive/TabContext';

loader.config({
  paths: {
    vs: UniStorageFactory.UniStorage.BackendUri + '/js/libs/monaco/min/vs',
  },
});

type Props = {
  /**
   * Set of typescript declarations (variables, functions, types) which is used for code completion (intellisense)
   * e.g. "declare const x = 1; declare function test(): string;"
   */
  intellisenseContent: string;
  defaultExpressionValue?: string;
  error?: boolean;
  errorText?: string;
  onExpressionChanged?: (expression: string) => void;
  disableValidation?: boolean;
  height?: number; // editor's height in px
  disabled?: boolean;
  isReadOnly?: boolean;
};

/*
 * Do not use this component directly!
 * Please use MonacoExpressionBuilderLazy!
 */
const MonacoExpressionBuilderNonLazy: React.FC<Props> = (props: Props) => {
  const {
    errorText,
    error,
    intellisenseContent,
    disableValidation,
    disabled,
    isReadOnly,
  } = props;
  const monaco = useMonaco();
  const tabId = useTabId();
  useTabFocusEffect(
    () => {
      if (!monaco || !intellisenseContent) {
        return;
      }
      const timeInMs = new Date().getTime();
      // for how to add extra lib to Monaco
      // see https://github.com/microsoft/monaco-editor/issues/3580#issuecomment-1975399214
      const filename = `customExpressions-${timeInMs}.ts`;
      const monacoModel = monaco.editor.createModel(
        intellisenseContent,
        'typescript',
        monaco.Uri.file(filename),
      );
      const extraLibRemovalFunc =
        monaco.languages.typescript.typescriptDefaults.addExtraLib(
          monacoModel.getValue(),
          monaco.Uri.file(filename).toString(),
        );
      return () => {
        extraLibRemovalFunc.dispose();
        monacoModel.dispose();
      };
    },
    [monaco, intellisenseContent],
    'monaco' + tabId,
  );

  return (
    <div className={styles.editor} data-error={error} data-editable={!disabled}>
      <Editor
        loading={null}
        options={{
          domReadOnly: disabled ?? isReadOnly,
          readOnly: disabled ?? isReadOnly,
          fixedOverflowWidgets: true,
          wordWrap: 'off',
          lineNumbers: 'off',
          lineNumbersMinChars: 0,
          overviewRulerLanes: 0,
          overviewRulerBorder: false,
          lineDecorationsWidth: 0,
          hideCursorInOverviewRuler: true,
          glyphMargin: false,
          folding: false,
          scrollBeyondLastColumn: 0,
          scrollbar: { horizontal: 'hidden', vertical: 'hidden' },
          find: {
            addExtraSpaceOnTop: false,
            autoFindInSelection: 'never',
            seedSearchStringFromSelection: 'never',
          },
          minimap: { enabled: false },
          renderLineHighlight: 'none',
          suggest: {
            showConstructors: false,
            showDeprecated: false,
            showVariables: false,
            showClasses: false,
            showStructs: false,
            showInterfaces: false,
            showModules: false,
            showEvents: false,
            showOperators: false,
            showUnits: false,
            showConstants: false,
            showEnums: false,
            showEnumMembers: false,
            showKeywords: false,
            showWords: false,
            showColors: false,
            showFiles: false,
            showReferences: false,
            showFolders: false,
            showIssues: false,
            showUsers: false,
            showSnippets: false,
          },
          contextmenu: false,
        }}
        defaultLanguage={'typescript'}
        value={props.defaultExpressionValue}
        onChange={(value) => {
          props.onExpressionChanged?.(value ?? '');
        }}
        onMount={(editor, monaco) => {
          if (disableValidation) {
            monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(
              {
                noSemanticValidation: true,
                noSyntaxValidation: true,
              },
            );
          }
          monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
            lib: [],
            allowNonTsExtensions: true,
            strict: true,
          });
          editor.onKeyDown((e: any) => {
            if (e.keyCode == monaco.KeyCode.Enter) {
              // We only prevent enter when the suggest model is not active
              if (
                (
                  editor.getContribution(
                    'editor.contrib.suggestController',
                  ) as any
                ).model.state == 0
              ) {
                e.preventDefault();
              }
            }
          });
          editor.onKeyUp((e: any) => {
            if (e.keyCode === monaco.KeyCode.Quote) {
              editor.trigger('', 'editor.action.triggerSuggest', '');
            }
          });
          editor.onDidPaste((e: any) => {
            if (e.range.endLineNumber > 1) {
              let newContent = '';
              const model = editor.getModel();
              if (!model) {
                return;
              }
              const lineCount = model.getLineCount();
              for (let i = 0; i < lineCount; i++) {
                newContent += model.getLineContent(i + 1);
              }
              model.setValue(newContent);
            }
          });
        }}
        height={props.height ?? 50}
      />
      {!!errorText && (
        <div data-error={error} className={styles.error}>
          {errorText}
        </div>
      )}
    </div>
  );
};

const Wrapper: React.FC<Props> = (props: Props) => {
  const [isLoaded, setIsLoaded] = useState(true);
  const [error, setError] = useState('');
  // disabled temporarily, since it doesn't work with webpack
  // useMemo(() => {
  //   initMonaco()
  //     .then((x) => {
  //       setIsLoaded(true);
  //     })
  //     .catch((e) => setError(e.message));
  // }, []);

  if (error) return <>error</>;
  if (!isLoaded) return null;
  return <MonacoExpressionBuilderNonLazy {...props} />;
};

export default Wrapper;
